/**
 * Shared text processing utilities for EnrichedAIResponse components.
 * Used by both EnrichedAIResponse.tsx and EnrichedAIResponseV3.tsx.
 */

import { THINKING_BLOCK_END, ANALYSIS_BLOCK_END } from './CollapsibleBlockV3';

export const stripUnclosedMermaidTag = (text: string): string => {
  const lines = text.split('\n');
  const lastMermaidTagIndex = lines.findLastIndex(line => line.trim() === '```mermaid');
  const nextClosingTagIndex = lines.findIndex((line, index) => {
    if (lastMermaidTagIndex >= 0 && index > lastMermaidTagIndex) {
      return line.trim() === '```';
    }
    return false;
  });
  // if we found a mermaid tag that is not followed by a closing tag ```
  // then we want to remove the "mermaid" from the opening tag
  // so that it renders as a plain code block until we have the full closing tag
  const shouldRemoveLastMermaidTag = lastMermaidTagIndex >= 0 && nextClosingTagIndex < 0;

  return lines
    .map((line, index) => {
      if (index === lastMermaidTagIndex && shouldRemoveLastMermaidTag) {
        return line.replace('```mermaid', '```');
      }
      return line;
    })
    .join('\n');
};

// React markdown does not support HTML, so we replace <thinking>...</thinking> tags with
// a custom code block and then render it as a reasoning block.
// This is because HTML is not valid markdown
// The alternative to render HTML in react markdown is to use the rehype-raw plugin
// but this would make the markdown renderer more complex
const THINKING_OPEN_FENCE = '```thinking';
const THINKING_CLOSE_FENCE = `${THINKING_BLOCK_END}\n\`\`\``;

export const processReasoningTags = (text: string): string => {
  const lines = text.split('\n');
  const result: string[] = [];
  let insideThinking = false;

  // We add proper indentation to the content within thinking blocks: the reasoning
  // block can contain other code blocks, and without indentation the nested code
  // blocks would interfere with markdown parsing.
  //
  // Robustness note: the backend may emit an unbalanced sequence of tags — e.g. a
  // transient failure that is retried re-emits <thinking> without ever closing the
  // aborted one, producing "<thinking>…<thinking>…</thinking>answer". We must never
  // let an un-closed block swallow the answer into the collapsible. Two rules handle
  // every case the model/backend can produce:
  //   1. A new <thinking> while a block is still open implicitly closes the previous
  //      block first (the model never legitimately nests thinking).
  //   2. </thinking> only closes when a block is actually open (a stray close is left
  //      as-is rather than emitting an orphan code fence).
  for (let i = 0; i < lines.length; i += 1) {
    let line = lines[i];
    let openedThisLine = false;

    if (line.startsWith('<thinking>')) {
      const opening = `${insideThinking ? `${THINKING_CLOSE_FENCE}\n` : ''}${THINKING_OPEN_FENCE}`;
      openedThisLine = true;
      insideThinking = true;
      line = line.replace('<thinking>', opening);
      // If there's content after the tag on the same line, move it to an indented line.
      if (line.length > opening.length) {
        line = line.replace(THINKING_OPEN_FENCE, `${THINKING_OPEN_FENCE}\n\t`);
      }
    }
    if ((insideThinking || openedThisLine) && line.includes('</thinking>')) {
      // Replace the closing tag with the end-of-block marker + fence. The marker lets
      // the renderer collapse the block once it is complete.
      line = line.replace('</thinking>', `${openedThisLine ? '\n' : ''}${THINKING_CLOSE_FENCE}`);
      insideThinking = false;
    }
    // Indent content that sits inside an open thinking block (but not the opening line).
    if (insideThinking && !openedThisLine) {
      line = `\t${line}`;
    }

    result.push(line);
  }

  return result.join('\n');
};

// No g flag on constants — add g only at call sites to avoid stateful lastIndex bugs.
// \r?\n handles both Unix and Windows line endings from the AI stream.
// Group 1 = optional title, group 2 = optional description, group 3 = prompt.
const PANTHER_PROMPT_BLOCK_RE =
  /```panther\r?\n(?:\s*title="([^"]+)"\r?\n)?(?:\s*description="([^"]+)"\r?\n)?\s*prompt="([^"]+)"\r?\n```/;

// Matches the Recommended Prompts / Next Steps section from its heading to (but not including)
// the next ## heading. Falls back to end-of-string if no subsequent heading exists.
// Note: [\s\S]*? is non-greedy; (?![\s\S]) is true only at end-of-string (JS has no \z).
const RECOMMENDED_SECTION_RE =
  /^(?:#{1,3}\s+(?:Recommended Prompts|Next Steps)|\*\*(?:Recommended Prompts|Next Steps)\*\*)[\s\S]*?(?=\n#{1,3} |(?![\s\S]))/im;

export interface RecommendedAction {
  title?: string;
  description?: string;
  prompt: string;
}

export const extractPantherPrompts = (text: string): RecommendedAction[] => {
  const actions: RecommendedAction[] = [];
  const re = new RegExp(PANTHER_PROMPT_BLOCK_RE.source, 'g');
  let match: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((match = re.exec(text)) !== null) {
    actions.push({
      title: match[1] || undefined,
      description: match[2] || undefined,
      prompt: match[3],
    });
  }
  return actions;
};

export const stripRecommendedSection = (text: string): string =>
  text.replace(RECOMMENDED_SECTION_RE, '').trimEnd();

// Normalizes panther code blocks with prompts into bullet points (used for clipboard copy)
export const normalizePantherPrompts = (text: string): string => {
  const re = new RegExp(PANTHER_PROMPT_BLOCK_RE.source, 'g');
  return text.replace(re, (_match, _title, _desc, prompt) => `- ${prompt}`);
};

// Extracts markdown content after the analysis block ends
export const extractContentAfterAnalysis = (text: string): string => {
  const lines = text.split('\n');

  // Find the end of the analysis block
  const analysisEndIndex = lines.findIndex(line => line === ANALYSIS_BLOCK_END);

  if (analysisEndIndex === -1) {
    // If there's no analysis block end marker, normalize and return the full text
    return text;
  }

  // Find the closing ``` after the analysis block end marker
  let closingBackticksIndex = -1;
  for (let i = analysisEndIndex + 1; i < lines.length; i += 1) {
    if (lines[i].trim() === '```') {
      closingBackticksIndex = i;
      break;
    }
  }

  // Extract everything after the closing ``` of the analysis block
  const startIndex =
    closingBackticksIndex !== -1 ? closingBackticksIndex + 1 : analysisEndIndex + 1;
  const contentAfterAnalysis = lines.slice(startIndex).join('\n').trim();

  return contentAfterAnalysis;
};

// This function wraps the analysis block until the next heading
// This is to ensure that the analysis block is rendered as a collapsible block
// and not as a heading
export const wrapAnalysisBlock = (text: string) => {
  const lines = text.split('\n');
  const analysisBlockIndex = lines.findIndex(line => line === '## Analysis');
  if (analysisBlockIndex === -1) {
    return text;
  }
  const textBeforeAnalysisBlock = lines.slice(0, analysisBlockIndex).join('\n');

  // we need to wrap the analysis block until the next heading
  // but we need to ignore # characters inside code blocks
  let insideCodeBlock = false;
  let nextHeadingIndex = -1;

  for (let i = analysisBlockIndex + 1; i < lines.length; i += 1) {
    const line = lines[i];

    // Check if we're entering or exiting a code block
    if (line.trim().startsWith('```')) {
      insideCodeBlock = !insideCodeBlock;
    }
    // Only look for headings when we're not inside a code block
    if (!insideCodeBlock && (line.startsWith('# ') || line.startsWith('## '))) {
      nextHeadingIndex = i;
      break;
    }
  }

  const textAfterAnalysisBlock =
    nextHeadingIndex === -1 ? '' : lines.slice(nextHeadingIndex).join('\n');

  let analysisBlock = lines
    .slice(analysisBlockIndex, nextHeadingIndex === -1 ? undefined : nextHeadingIndex)
    .filter(line => line !== '## Analysis')
    .map(line => `\t${line}`)
    .join('\n');
  if (nextHeadingIndex !== -1) {
    analysisBlock = `${analysisBlock}\n${ANALYSIS_BLOCK_END}`;
  }

  return `${textBeforeAnalysisBlock}\n\`\`\`analysis\n${analysisBlock}\n\`\`\`\n${textAfterAnalysisBlock}`;
};

export const getNextChunkForTypewriterEffect = (
  fullText: string,
  currentlyDisplayedText: string,
) => {
  // If the next character a mermaid, panther, thinking, analysis, or riskscore code block tag, we render it all at once
  const remainingText = fullText.substring(currentlyDisplayedText.length);
  const nextIsCodeBlock = remainingText.match(/^```(panther|mermaid|thinking|analysis|riskscore)/);
  if (nextIsCodeBlock) {
    const indexOfCodeBlockEnding = remainingText.indexOf('\n```');
    if (indexOfCodeBlockEnding === -1) {
      return fullText;
    }

    return fullText.substring(0, currentlyDisplayedText.length + indexOfCodeBlockEnding + 4);
  }

  // render the next word or phrase
  const nextWhitespaceIndex = remainingText.search(/\s/);
  if (nextWhitespaceIndex === -1) {
    return fullText;
  }
  return fullText.substring(0, currentlyDisplayedText.length + nextWhitespaceIndex + 1);
};
